import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useFeatureSwitchService } from '../services/featureSwitchService';
import { featureSwitchKeys, useFeatureSwitchesView } from '../../../shared/hooks/useFeatureSwitch';
import type { FeatureSwitchMove, SetFeatureSwitchRequest, SwitchDetail } from '../../../types/backend';

export interface FeatureSwitchAdmin {
  /** null: the caller is not a platform admin, so the server returned no details. */
  details: SwitchDetail[] | null;
  /** The staff groups a switch may be enabled for. */
  availableGroups: string[];
  loading: boolean;
  error: string | null;
  canSet: boolean;
  set: (request: SetFeatureSwitchRequest) => Promise<FeatureSwitchMove>;
}

export function useFeatureSwitchAdmin(): FeatureSwitchAdmin {
  const service = useFeatureSwitchService();
  const queryClient = useQueryClient();
  const { view, error, loading } = useFeatureSwitchesView();

  // A change alters what every caller resolves, the admin's own switches included.
  const setMutation = useMutation({
    mutationFn: (request: SetFeatureSwitchRequest) => service.setFeatureSwitch(request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: featureSwitchKeys.all() }),
  });

  return {
    // The generated types drop the backend's `| None`; a caller who cannot set switches gets null.
    details: view?.details ?? null,
    availableGroups: view?.available_groups ?? [],
    loading,
    error: error ? error.message : null,
    // The server's Cedar answer for ops.SetFeatureSwitch; absent or failed reads offer no controls.
    canSet: view?.can_set === true,
    set: setMutation.mutateAsync,
  };
}
